// orchestrator_fullstack/frontend/src/app/auth/api.ts
import { AUTH_STORAGE_KEY, AuthMe } from "./rbac";

export class ApiError extends Error {
  status: number;
  traceId?: string;
  body?: any;
  detail?: any;

  constructor(message: string, status: number, traceId?: string, body?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.traceId = traceId;
    this.body = body;
    this.detail = body?.detail ?? body;
  }
}

function getApiBase(): string {
  // Canonical: same-origin behind nginx (web container proxies /v1/* -> api:8000)
  // Override only for non-nginx dev setups.
  const v = (import.meta as any).env?.VITE_API_BASE;
  const s = v ? String(v).trim() : "";
  return s; // allow "" (same-origin)
}

function joinUrl(base: string, path: string): string {
  const b = (base || "").trim();
  const p = path.startsWith("/") ? path : `/${path}`;

  if (!b) return p;

  // Avoid double /v1 prefix if base is "/v1" and path already begins with "/v1/"
  if (b.endsWith("/v1") && p.startsWith("/v1/")) {
    const b2 = b.slice(0, -3) || ""; // remove trailing "/v1"
    return `${b2}${p}`;
  }

  return `${b}${p}`;
}

export function getToken(): string | null {
  const t = localStorage.getItem(AUTH_STORAGE_KEY);
  return t && t.trim() ? t.trim() : null;
}

export function setToken(token: string | null) {
  if (!token) localStorage.removeItem(AUTH_STORAGE_KEY);
  else localStorage.setItem(AUTH_STORAGE_KEY, token);
}

async function readBodySafe(resp: Response): Promise<any> {
  const ct = resp.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    try {
      return await resp.json();
    } catch {
      return null;
    }
  }
  try {
    const text = await resp.text();
    return text ? { raw: text } : null;
  } catch {
    return null;
  }
}

export async function apiFetch<T>(
  path: string,
  opts: {
    method?: string;
    body?: any;
    headers?: Record<string, string>;
    xTenantId?: string;
    admin?: boolean;
  } = {}
): Promise<T> {
  const base = getApiBase();
  const url = /^https?:\/\//i.test(path) ? path : joinUrl(base, path);

  const headers: Record<string, string> = { ...(opts.headers || {}) };

  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  if (!opts.admin && opts.xTenantId) {
    headers["X-Tenant-ID"] = opts.xTenantId;
  }

  let body: any = undefined;
  if (opts.body !== undefined) {
    headers["Content-Type"] = headers["Content-Type"] || "application/json";
    body = typeof opts.body === "string" ? opts.body : JSON.stringify(opts.body);
  }

  const resp = await fetch(url, { method: opts.method || "GET", headers, body });

  const traceId = resp.headers.get("x-trace-id") || undefined;

  if (resp.status === 204) return undefined as any;

  const data = await readBodySafe(resp);

  if (!resp.ok) {
    let msg: any =
      (data && (data.detail ?? data.message ?? data.error)) ||
      `HTTP ${resp.status} ${resp.statusText}`;

    if (msg && typeof msg === "object") {
      msg = msg.message || msg.code || JSON.stringify(msg);
    }

    throw new ApiError(String(msg), resp.status, traceId, data);
  }

  return data as T;
}

// ---- Convenience helpers ----


export async function authMe(): Promise<AuthMe> {
  const me = await apiFetch<AuthMe>("/v1/auth/me", { admin: false });

  // Back-compat: older modules read tenant_uuid/tenant_label from localStorage.
  // This MUST be set so non-root tenants don't fall back to root.
  try {
    const tenantUuid = typeof (me as any)?.tenant === "string" ? (me as any).tenant : "";
    if (tenantUuid && tenantUuid.length >= 32) {
      localStorage.setItem("tenant_uuid", tenantUuid);
    }
    // Optional display-only label.
    // IMPORTANT: do NOT force "root" — that causes non-root tenants to render as "root" in legacy UI.
    // We'll only set tenant_label if something already stored it (or you later populate it correctly).
    const existingLabel = localStorage.getItem("tenant_label");
    if (existingLabel && existingLabel.trim()) {
      // keep existing
    } else {
      // leave unset; UI can fall back to showing UUID or fetch label from /v1/admin/tenants (platform-admin)
    }
  } catch {
    // never block auth flow on storage errors
  }

  return me;
}

// Back-compat export used by AuthProvider.tsx.
// Keep it calling authMe() so we preserve localStorage side-effects.
export async function fetchAuthMe(): Promise<AuthMe> {
  return authMe();
}

export type ProvisionTenantResponse = {
  tenant_uuid: string;
  tenant_slug: string;
  tenant_name: string;
  roles: string[];
};

export async function provisionTenant(payload: { tenant_name: string; tenant_slug?: string }) {
  return apiFetch<ProvisionTenantResponse>("/v1/auth/provision/tenant", {
    method: "POST",
    body: payload,
    admin: false,
  });
}